/**
 * モバイルドロワー（1023px 以下でサイドバーの代わりに使う）
 *
 * マークアップ規約:
 *   <button data-lms-drawer-toggle aria-controls="lms-drawer">…</button>
 *   <div id="lms-drawer" class="lms-drawer-overlay" data-lms-drawer hidden>
 *     <div class="lms-drawer">…サイドバーと同じ中身…</div>
 *   </div>
 *
 * 挙動:
 *   - オーバーレイクリック・ESC・ドロワー内リンククリックで閉じる
 *   - 1024px 以上に広がったら自動で閉じる（サイドバーが常設に戻るため）
 *   - 開くときにヘッダーのユーザーメニュー（ドロップダウン）を閉じる。ユーザーメニューと
 *     ドロワーを同時に開いた状態にしないため（polish-menu-label-calendar D2 / auth spec
 *     「ユーザーメニューとドロワーが同時に開いた状態にしてはならない」）。逆向き（メニューを
 *     開いたらドロワーを閉じる）は相互 import を避けるため main.ts が結線する
 *
 * 多重初期化のガード（polish-menu-label-calendar レビューサイクル 1 対応 typescript-reviewer
 * Medium-1）: `dropdown.ts` と同じ 2 段構え。要素側リスナー（ドロワー本体のクリック・トグル
 * ボタンのクリック）は要素の dataset フラグで、`document` / `matchMedia` へのグローバル
 * リスナーはモジュールスコープの `globalListenersBound` でそれぞれガードする。`initDrawer()`
 * が同じ要素に対して複数回呼ばれても listener が積み上がらない。
 */

import { closeAllDropdowns } from "./dropdown.js";

const DESKTOP_QUERY = "(min-width: 1024px)";

let drawer: HTMLElement | null = null;
let globalListenersBound = false;

function isOpen(): boolean {
  return drawer !== null && !drawer.hidden;
}

export function openDrawer(): void {
  if (!drawer) return;
  closeAllDropdowns();
  drawer.hidden = false;
  document.body.style.overflow = "hidden";
  document.querySelectorAll<HTMLElement>("[data-lms-drawer-toggle]").forEach((b) => {
    b.setAttribute("aria-expanded", "true");
    b.classList.add("is-open"); // バーガー ✕ 変形（SHELL-R3）
  });
}

export function closeDrawer(): void {
  if (!drawer) return;
  drawer.hidden = true;
  document.body.style.overflow = "";
  document.querySelectorAll<HTMLElement>("[data-lms-drawer-toggle]").forEach((b) => {
    b.setAttribute("aria-expanded", "false");
    b.classList.remove("is-open"); // バーガーを 3 本線へ戻す
  });
}

/** `document` / `matchMedia` へのグローバルリスナーを 1 回だけ登録する（dropdown.ts と同じ流儀）。 */
function bindGlobalListenersOnce(): void {
  if (globalListenersBound) return;
  globalListenersBound = true;

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen()) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // デスクトップ幅に戻ったら閉じる
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", (e) => {
    if (e.matches) closeDrawer();
  });
}

/** {@code scope} 内の {@code [data-lms-drawer]} を初期化する。多重初期化は dataset フラグでガードする。 */
export function initDrawer(scope: ParentNode = document): void {
  // 部分更新後の再初期化で scope にドロワーが無い場合、初期化済みの参照を壊さない
  const found = scope.querySelector<HTMLElement>("[data-lms-drawer]");
  if (!found) return;
  drawer = found;

  if (drawer.dataset["lmsDrawerReady"] !== "1") {
    drawer.dataset["lmsDrawerReady"] = "1";
    drawer.hidden = true;

    drawer.addEventListener("click", (e) => {
      if (e.target === drawer) {
        closeDrawer();
        return;
      }
      // ナビゲーションしたらドロワーを閉じる
      if (e.target instanceof Element && e.target.closest("a[href]")) {
        closeDrawer();
      }
    });
  }

  scope.querySelectorAll<HTMLElement>("[data-lms-drawer-toggle]").forEach((btn) => {
    if (btn.dataset["lmsDrawerToggleReady"] === "1") return;
    btn.dataset["lmsDrawerToggleReady"] = "1";
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      if (isOpen()) closeDrawer();
      else openDrawer();
    });
  });

  bindGlobalListenersOnce();
}
